import OrderStatesEnum from "../../../models/OrderStatesEnum";

export type OrderStatusConfig = {
    bgClass: string;
    textClass: string;
    icon?: string;
};

const DEFAULT_CONFIG: OrderStatusConfig = {
    bgClass: "bg-secondary",
    textClass: "text-light",
};

export const ORDER_STATUS_CONFIG: Partial<Record<OrderStatesEnum, OrderStatusConfig>> = {
    // Success/Complete states - Green
    [OrderStatesEnum.COMPLETED]: { bgClass: "bg-success", textClass: "text-light" },
    [OrderStatesEnum.DELIVERED]: { bgClass: "bg-success", textClass: "text-light" },
    [OrderStatesEnum.PICKED_UP]: { bgClass: "bg-success", textClass: "text-light" },
    [OrderStatesEnum.APPROVED]: { bgClass: "bg-success", textClass: "text-light" },
    [OrderStatesEnum.CLOSED]: { bgClass: "bg-success", textClass: "text-light" },
    [OrderStatesEnum.BILLED]: { bgClass: "bg-success", textClass: "text-light" },

    // Cancelled/Error - Red
    [OrderStatesEnum.CANCELLED]: { bgClass: "bg-danger", textClass: "text-light" },

    // Warning/Attention - Yellow
    [OrderStatesEnum.DELAYED]: { bgClass: "bg-warning", textClass: "text-dark" },
    [OrderStatesEnum.AWAITING_REVIEW]: { bgClass: "bg-warning", textClass: "text-dark" },
    [OrderStatesEnum.UNDER_REVIEW]: { bgClass: "bg-warning", textClass: "text-dark" },
    [OrderStatesEnum.PENDING]: { bgClass: "bg-warning", textClass: "text-dark" },
    [OrderStatesEnum.AWAITING_FINAL_ACCOUNT_APPROVAL]: { bgClass: "bg-warning", textClass: "text-dark" },

    // In Progress states - Info blue
    [OrderStatesEnum.IN_REVIEW]: { bgClass: "bg-info", textClass: "text-dark" },
    [OrderStatesEnum.SCHEDULED]: { bgClass: "bg-info", textClass: "text-dark" },
    [OrderStatesEnum.IN_PROGRESS_PACKING]: { bgClass: "bg-info", textClass: "text-dark" },
    [OrderStatesEnum.IN_QUEUE]: { bgClass: "bg-info", textClass: "text-dark" },
    [OrderStatesEnum.IN_DESIGN]: { bgClass: "bg-info", textClass: "text-dark" },
    [OrderStatesEnum.PRINTING]: { bgClass: "bg-info", textClass: "text-dark" },
    [OrderStatesEnum.SEWING]: { bgClass: "bg-info", textClass: "text-dark" },
    [OrderStatesEnum.MATERIAL_CUTTING]: { bgClass: "bg-info", textClass: "text-dark" },
    [OrderStatesEnum.SHIPPED]: { bgClass: "bg-info", textClass: "text-dark" },
    [OrderStatesEnum.PARTIALLY_SHIPPED]: { bgClass: "bg-info", textClass: "text-dark" },
    [OrderStatesEnum.CONFIRMED]: { bgClass: "bg-info", textClass: "text-dark" },
    [OrderStatesEnum.RECEIVED]: { bgClass: "bg-info", textClass: "text-dark" },
    [OrderStatesEnum.TRANSFERRED]: { bgClass: "bg-info", textClass: "text-dark" },

    // Ready states - Primary blue
    [OrderStatesEnum.READY_FOR_PICKUP]: { bgClass: "bg-primary", textClass: "text-light" },
    [OrderStatesEnum.READY_TO_SHIP]: { bgClass: "bg-primary", textClass: "text-light" },
    [OrderStatesEnum.READY_FOR_DELIVERY]: { bgClass: "bg-primary", textClass: "text-light" },
    [OrderStatesEnum.READY_FOR_PICK]: { bgClass: "bg-primary", textClass: "text-light" },
    [OrderStatesEnum.READY_TO_PRINT]: { bgClass: "bg-primary", textClass: "text-light" },

    // Processing states - Secondary gray
    [OrderStatesEnum.SUBMITTED]: { bgClass: "bg-secondary", textClass: "text-light" },
    [OrderStatesEnum.PROCESSED]: { bgClass: "bg-secondary", textClass: "text-light" },
    [OrderStatesEnum.PRINTED]: { bgClass: "bg-secondary", textClass: "text-light" },
    [OrderStatesEnum.MOUNTED]: { bgClass: "bg-secondary", textClass: "text-light" },
    [OrderStatesEnum.LAMINATED]: { bgClass: "bg-secondary", textClass: "text-light" },
    [OrderStatesEnum.FINISHING]: { bgClass: "bg-secondary", textClass: "text-light" },
    [OrderStatesEnum.PRE_PRESS]: { bgClass: "bg-secondary", textClass: "text-light" },
    [OrderStatesEnum.WAREHOUSE]: { bgClass: "bg-secondary", textClass: "text-light" },

    // Initial/New states - Light with dark text
    [OrderStatesEnum.PRELIMINARY]: { bgClass: "bg-light", textClass: "text-dark" },
    [OrderStatesEnum.NEW]: { bgClass: "bg-light", textClass: "text-dark" },
    [OrderStatesEnum.OPEN]: { bgClass: "bg-light", textClass: "text-dark" },
    [OrderStatesEnum.ACTIVE]: { bgClass: "bg-light", textClass: "text-dark" },

    // Payment/Accounting states - Purple
    [OrderStatesEnum.VENDOR_APPROVAL_PAYMENT]: { bgClass: "bg-purple", textClass: "text-light" },
    [OrderStatesEnum.ACCOUNTING]: { bgClass: "bg-purple", textClass: "text-light" },
    [OrderStatesEnum.PAYMENT_RECEIVED_ORDER_BEING_PACKED]: { bgClass: "bg-purple", textClass: "text-light" },
    [OrderStatesEnum.DEPOSIT_RECEIVED_ORDER_PACKING]: { bgClass: "bg-purple", textClass: "text-light" },
    [OrderStatesEnum.PACKING_COMPLETED_AWAITING_PAYMENT_BALANCE_TO_SHIP]: { bgClass: "bg-purple", textClass: "text-light" },
    [OrderStatesEnum.VENDOR_SOURCE_APPROVAL_AWAITING_PAYMENT]: { bgClass: "bg-purple", textClass: "text-light" },
    [OrderStatesEnum.PACKING_COMPLETED_AWAITING_SHIPPING_INFO]: { bgClass: "bg-secondary", textClass: "text-light" },

    // Production states - Teal
    [OrderStatesEnum.SUBMITTED_TO_PRODUCTION]: { bgClass: "bg-teal", textClass: "text-light" },
    [OrderStatesEnum.WELD_COMPLETED]: { bgClass: "bg-teal", textClass: "text-light" },
    [OrderStatesEnum.INITIAL_QC_COMPLETED]: { bgClass: "bg-teal", textClass: "text-light" },
    [OrderStatesEnum.PAINT_COMPLETED]: { bgClass: "bg-teal", textClass: "text-light" },
    [OrderStatesEnum.FINAL_QC_COMPLETED]: { bgClass: "bg-teal", textClass: "text-light" },
    [OrderStatesEnum.PARTS_ORDERED]: { bgClass: "bg-teal", textClass: "text-light" },

    // Other specific states
    [OrderStatesEnum.SENT_TO_PHARMACY]: { bgClass: "bg-info", textClass: "text-dark" },
};

export const getStatusConfig = (id: OrderStatesEnum): OrderStatusConfig => {
    return ORDER_STATUS_CONFIG[id] || DEFAULT_CONFIG;
};

export const formatStatusName = (enumValue: OrderStatesEnum): string => {
    const enumKey = OrderStatesEnum[enumValue];
    if (!enumKey) return "Unknown";

    return enumKey
        .split("_")
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join(" ");
};
